import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Monitor, Smartphone, Tablet, Lock, ShieldCheck } from "lucide-react";
import clsx from "clsx";
import { Fade } from "react-awesome-reveal";

import SectionTitle from "../home/SectionTitle";

const ROWS = [
  { Icon: Globe, nameKey: "trading.platform.r1.name", tabKey: "trading.platform.r1.tab", purposeKey: "trading.platform.r1.purpose", benefitKey: "trading.platform.r1.benefit" },
  { Icon: Monitor, nameKey: "trading.platform.r2.name", tabKey: "trading.platform.r2.tab", purposeKey: "trading.platform.r2.purpose", benefitKey: "trading.platform.r2.benefit" },
  { Icon: Smartphone, nameKey: "trading.platform.r3.name", tabKey: "trading.platform.r3.tab", purposeKey: "trading.platform.r3.purpose", benefitKey: "trading.platform.r3.benefit" },
  { Icon: Tablet, nameKey: "trading.platform.r4.name", tabKey: "trading.platform.r4.tab", purposeKey: "trading.platform.r4.purpose", benefitKey: "trading.platform.r4.benefit" },
  { Icon: Lock, nameKey: "trading.platform.r5.name", tabKey: "trading.platform.r5.tab", purposeKey: "trading.platform.r5.purpose", benefitKey: "trading.platform.r5.benefit" },
  { Icon: ShieldCheck, nameKey: "trading.platform.r6.name", tabKey: "trading.platform.r6.tab", purposeKey: "trading.platform.r6.purpose", benefitKey: "trading.platform.r6.benefit" },
];

function PlatformAccess() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const activeRow = ROWS[active];

  return (
    <section className="mt">
      <div className="glcontainer">
        <SectionTitle
          title1={t("trading.platform.title1")}
          title2={t("trading.platform.title2")}
          text={t("trading.platform.subtitle")}
          className="mx-auto max-w-[525px]"
        />

        {/* Desktop: таблица */}
        <Fade triggerOnce direction="up" duration={700} className="hidden lg:block mt-[50px]">
        <div className="rounded-[24px] border border-[#e4edf7] overflow-hidden bg-white">
          <div className="flex items-stretch bg-[#f8fbff] h-[68px]">
            <div className="w-[72px] shrink-0 border-r border-[#e4edf7]" />
            <p className="w-[326px] shrink-0 flex items-center px-[24px] font-['Inter'] font-bold text-[16px] text-[#1c385f] border-r border-[#e4edf7]">
              {t("trading.platform.col1")}
            </p>
            <p className="w-[370px] shrink-0 flex items-center px-[24px] font-['Inter'] font-bold text-[16px] text-[#1c385f] border-r border-[#e4edf7]">
              {t("trading.platform.col2")}
            </p>
            <p className="flex-1 flex items-center px-[24px] font-['Inter'] font-bold text-[16px] text-[#1c385f]">
              {t("trading.platform.col3")}
            </p>
          </div>
          {ROWS.map((row, i) => (
            <div
              key={row.nameKey}
              className={clsx(
                "flex items-stretch h-[68px] border-t border-[#e4edf7]",
                i === 0 || i === 4 ? "bg-[#f8faff]" : "bg-white"
              )}
            >
              <div className="w-[72px] shrink-0 flex items-center justify-center border-r border-[#e4edf7]">
                <row.Icon className="w-[26px] h-[26px] text-[#1c385f]" strokeWidth={1.7} />
              </div>
              <p className="w-[326px] shrink-0 flex items-center px-[24px] font-['Inter'] font-bold text-[16px] text-[#1c385f] border-r border-[#e4edf7]">
                {t(row.nameKey)}
              </p>
              <p className="w-[370px] shrink-0 flex items-center px-[24px] font-['Inter'] font-normal text-[16px] text-[#435c7f] border-r border-[#e4edf7]">
                {t(row.purposeKey)}
              </p>
              <p className="flex-1 flex items-center px-[24px] font-['Inter'] font-normal text-[16px] text-[#435c7f]">
                {t(row.benefitKey)}
              </p>
            </div>
          ))}
        </div>
        </Fade>

        {/* Mobile / tablet: табы + карточка */}
        <div className="lg:hidden mt-[25px]">
          <div className="flex flex-wrap justify-center gap-[6px]">
            {ROWS.map((row, i) => (
              <button
                key={row.tabKey}
                type="button"
                onClick={() => setActive(i)}
                className={clsx(
                  "flex items-center justify-center h-[40px] px-[16px] rounded-full font-['Inter'] font-bold text-[14px] transition-colors duration-200",
                  active === i
                    ? "bg-[#1c385f] text-white"
                    : "bg-white border border-[#e4edf7] text-[#435c7f]"
                )}
              >
                {t(row.tabKey)}
              </button>
            ))}
          </div>

          <div className="mt-[25px] rounded-[20px] border border-[#e4edf7] bg-white p-[20px]">
            <div className="flex items-center gap-[12px]">
              <div className="w-[40px] h-[40px] rounded-[12px] bg-[#f8faff] flex items-center justify-center shrink-0">
                <activeRow.Icon className="w-[22px] h-[22px] text-[#1c385f]" strokeWidth={1.7} />
              </div>
              <p className="font-['Inter'] font-bold text-[16px] text-[#1c385f]">
                {t(activeRow.nameKey)}
              </p>
            </div>
            <div className="mt-[16px]">
              <p className="font-['Inter'] font-medium text-[12px] uppercase tracking-[0.04em] text-[#8fa3bd]">
                {t("trading.platform.col2")}
              </p>
              <p className="mt-[4px] font-['Inter'] font-normal text-[14px] text-[#435c7f]">
                {t(activeRow.purposeKey)}
              </p>
            </div>
            <div className="mt-[12px] pt-[12px] border-t border-[#e4edf7]">
              <p className="font-['Inter'] font-medium text-[12px] uppercase tracking-[0.04em] text-[#8fa3bd]">
                {t("trading.platform.col3")}
              </p>
              <p className="mt-[4px] font-['Inter'] font-normal text-[14px] text-[#435c7f]">
                {t(activeRow.benefitKey)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PlatformAccess;
